/**
 * Утилиты для работы с номерами телефонов
 */

/**
 * Извлекает только цифры из форматированного номера телефона
 * "+7 (916) 123-45-67" -> "9161234567"
 */
export const extractPhoneDigits = (formattedPhone: string): string => {
  const allDigits = formattedPhone.replace(/\D/g, '');
  // Убираем код страны (7) или федеральный префикс (8) — оба варианта
  // встречаются при вводе/вставке российского номера.
  if (allDigits.length === 11 && (allDigits[0] === '7' || allDigits[0] === '8')) {
    return allDigits.slice(1);
  }
  return allDigits;
};

/**
 * Нормализует произвольную вставленную/введённую строку в 10 цифр
 * российского номера (без кода страны). Используется до того, как строка
 * попадёт в маску — сама маска не умеет распознавать лишний "8"/"+7" в начале
 * вставленного текста и просто сдвигает цифры, теряя последнюю.
 */
export const normalizeRussianPhoneDigits = (raw: string): string => {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 11 && (digits[0] === '7' || digits[0] === '8')) {
    return digits.slice(1, 11);
  }
  return digits.slice(0, 10);
};

/**
 * Валидация российского номера телефона
 * Должно быть 10 цифр после +7
 */
export const validatePhone = (phone: string): boolean => {
  const digits = phone.replace(/\D/g, '');
  return digits.length === 10;
};

/**
 * Извлекает чистые 10 цифр из unmaskedValue IMask (мы используем "+{7} ..." —
 * фигурные скобки заставляют IMask включать литерал "7" в unmaskedValue).
 * В отличие от extractPhoneDigits, здесь НЕЛЬЗЯ ориентироваться на общую
 * длину строки "11 цифр = есть код страны": unmaskedValue во время набора
 * растёт по одной цифре и при незаполненном последнем разряде (9 введённых
 * цифр) даёт как раз 10 символов ("7" + 9 цифр) — если бы мы проверяли
 * length === 11 для отсечения "7" (как делает extractPhoneDigits для готовой
 * отформатированной строки), недобранный на одну цифру номер ошибочно
 * прошёл бы валидацию как полный. unmaskedValue не содержит
 * плейсхолдеров-подчёркиваний, поэтому здесь достаточно отрезать
 * гарантированно первый символ — литерал "7".
 */
export const unmaskedToPhoneDigits = (unmaskedValue: string): string => {
  const digits = unmaskedValue.replace(/\D/g, '');
  return digits.startsWith('7') ? digits.slice(1) : digits;
};

/**
 * Форматирует чистый номер телефона в формат с маской
 * "9161234567" -> "+7 (916) 123-45-67"
 */
export const formatPhone = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, '');
  
  if (cleaned.length === 0) return '';
  if (cleaned.length <= 3) return `+7 (${cleaned}`;
  if (cleaned.length <= 6) return `+7 (${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  if (cleaned.length <= 8) return `+7 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  
  return `+7 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 8)}-${cleaned.slice(8, 10)}`;
};

/**
 * Проверяет, является ли номер полным (10 цифр)
 */
export const isPhoneComplete = (phone: string): boolean => {
  const digits = extractPhoneDigits(phone);
  return digits.length === 10;
};

/**
 * Очищает номер телефона для отправки на сервер
 * Возвращает только цифры без +7
 */
export const cleanPhoneForSubmit = (phone: string): string => {
  return extractPhoneDigits(phone);
};
